import { createTheme } from "@mui/material"

import { colors, theme as twTheme } from "@bullseye/tailwind-config"

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    link: true
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    tertiary: Palette["primary"]
  }

  interface PaletteOptions {
    tertiary: PaletteOptions["primary"]
  }
}
declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    tertiary: true
  }
}

const SPACING = 8
const breakpoints = {
  values: {
    xs: 0,
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1200,
    xxl: 1536,
  },
}

const theme = createTheme({
  spacing: SPACING,
  breakpoints,
})

const fonts = {
  h1: "MaisonNeueExtended",
  heading: "MaisonNeue",
  body: "MaisonNeue",
}

export default createTheme({
  spacing: SPACING,
  breakpoints,
  palette: {
    primary: {
      main: colors.primary[700],
      dark: colors.primary[900],
      light: colors.primary[200],
    },
    secondary: {
      main: colors.gray[950],
    },
    error: {
      main: colors.red[400],
      dark: colors.red[800],
      light: colors.red[50],
    },
    text: {
      primary: colors.gray[950],
      disabled: colors.gray[200],
    },
    tertiary: {
      main: colors.gray[100],
    },
  },
  typography: {
    fontFamily: fonts.body,
    h1: {
      fontFamily: fonts.h1,
      fontSize: twTheme.fontSize["5xl"],
      [theme.breakpoints.down("sm")]: {
        fontSize: twTheme.fontSize["4xl"],
      },
      fontWeight: 700,
    },
    h2: {
      fontSize: twTheme.fontSize["5xl"],
      fontWeight: 600,
      [theme.breakpoints.down("sm")]: {
        fontSize: twTheme.fontSize["2.5xl"],
      },
    },
    h3: {
      fontSize: twTheme.fontSize["2xl"],
      fontWeight: 600,
      [theme.breakpoints.down("sm")]: {
        fontSize: twTheme.fontSize["1.5xl"],
      },
    },
    h4: {
      fontSize: twTheme.fontSize["text-xl"],
      fontWeight: 600,
      [theme.breakpoints.down("sm")]: {
        fontSize: twTheme.fontSize["text-lg"],
      },
    },
    h5: {
      fontSize: twTheme.fontSize["text-xl"],
    },
    subtitle1: {
      fontSize: theme.spacing(18 / 8),
      fontWeight: 600,
    },
    subtitle2: {
      fontSize: theme.spacing(2),
      color: colors.gray[600],
      fontWeight: 600,
    },
    body1: {
      color: colors.gray[950],
    },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          lineHeight: "125%",
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: "none",
        color: "inherit",
      },
    },
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "14px",
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ ownerState: { variant } }) => ({
          ...(variant === "outlined" && {
            "&.Mui-focused, &.MuiFormLabel-filled": {
              transform: "translate(10px, 8px)",
              fontSize: "14px",
              lineHeight: 1,
            },
          }),
        }),
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          width: "100%",
          "& fieldset": {
            top: 0,
          },
          "& legend": {
            display: "none",
          },
          "& .MuiSelect-select": {
            height: "1rem",
          },
        },
        input: {
          "&:not(.MuiInputBase-inputHiddenLabel, .MuiInputBase-inputMultiline)":
            {
              padding: "30px 10px 10px 10px",
              height: "16px",
            },
        },
        inputMultiline: {
          padding: 0,
        },
        multiline: {
          "&.Mui-focused:not(.MuiInputBase-hiddenLabel), .MuiTextField-root.filled:not(.MuiInputBase-hiddenLabel) &":
            {
              padding: "26px 10px 7px",
            },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root:not(.MuiInputBase-hiddenLabel)": {
            padding: 0,
          },
          "& .MuiOutlinedInput-root": {
            backgroundColor: "white",
          },
          "& .MuiOutlinedInput-root .MuiAutocomplete-input:not(.MuiInputBase-inputHiddenLabel)":
            {
              padding: "30px 10px 10px 10px",
              height: "16px",
            },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: "link" },
          style: {
            padding: 0,
            fontWeight: 600,
            minWidth: "unset",
            textDecoration: "underline",
            "&:active, &:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
            "& > span.MuiTouchRipple-root": {
              display: "none",
            },
            "&.MuiButton-linkPrimary": {
              color: colors.primary[500],
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "1rem",
          boxShadow: "none",
          fontWeight: 600,
          "&:active, &:hover": {
            boxShadow: "none",
          },
        },
        sizeMedium: {
          borderRadius: "8px",
        },
        containedPrimary: {
          "&:focus": {
            backgroundColor: colors.primary[900],
            outline: "1px solid " + colors.primary[100],
          },
          "&:hover": {
            backgroundColor: colors.primary[700],
          },
          "&.Mui-disabled": {
            backgroundColor: colors.primary[100],
          },
          backgroundColor: colors.primary[500],
          color: colors.white,
        },
        containedSecondary: {
          "&:focus": {
            backgroundColor: colors.primary[100],
            outline: "1px solid " + colors.primary[500],
          },
          "&:hover": {
            backgroundColor: colors.primary[100],
          },
          "&.Mui-disabled": {
            backgroundColor: colors.gray[50],
            color: colors.gray[200],
          },
          backgroundColor: colors.primary[50],
          color: colors.primary[900],
        },
        contained: {
          "&.MuiButton-containedTertiary": {
            "&:focus": {
              backgroundColor: colors.gray[200],
              outline: "1px solid " + colors.primary[500],
            },
            "&:hover": {
              backgroundColor: colors.gray[200],
            },
            "&.Mui-disabled": {
              backgroundColor: colors.gray[50],
              color: colors.gray[200],
            },
            backgroundColor: colors.gray[100],
            color: colors.gray[950],
          },
        },
        outlinedSecondary: {
          "&:focus": {
            backgroundColor: colors.gray[100],
            border: "1px solid " + colors.gray[400],
          },
          "&:hover": {
            backgroundColor: colors.gray[50],
            border: "1px solid " + colors.gray[400],
          },
          "&.Mui-disabled": {
            color: colors.gray[200],
          },
          backgroundColor: colors.white,
          color: colors.gray[950],
          border: "1px solid " + colors.gray[200],
        },
      },
    },
    MuiGrid: {
      defaultProps: {
        borderColor: colors.gray[100],
      },
      styleOverrides: {
        root: {
          borderColor: colors.gray[100],
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderColor: colors.gray[100],
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "1rem",
          fontWeight: 600,
          padding: theme.spacing(0.25, 2, 2.75),
          [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(0.25, 0.5, 2.75),
            minWidth: "unset",
          },
        },
      },
    },
  },
})
